import React, { useState, useEffect } from 'react';

const AboutUsNew = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  const teamImages = [
    '/images/seamless_cloud_migrations.png',
    '/images/devops_optimization_service.png',
    '/images/finops_expertise.png',
    '/images/devsecops.png',
    '/images/disaster_recovery.png',
    '/images/cloud_native_kubernetes.png',
    '/images/hld_lld.png',
    '/images/discount.png',
    '/images/support.png',
    '/images/platform.png',
    '/images/mlops_gpu.png',
    '/images/robust_cicd.png',
    '/images/etl_pipelines.png'
  ];

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % teamImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [teamImages.length]);

  const stats = [
    { number: '70%', label: 'Improved Security' },
    { number: '99.9%', label: 'Uptime Guarantee' },
    { number: '24/7', label: 'Support Coverage' },
    { number: '60%', label: 'Cost Reduction' }
  ];

  const values = [
    {
      icon: '🚀',
      title: 'Innovation First',
      description: 'We push boundaries and embrace cutting-edge technologies to deliver solutions that keep you ahead of the curve.'
    },
    {
      icon: '🤝',
      title: 'Partnership Driven',
      description: 'Your success is our success. We work as an extension of your team, not just a vendor.'
    },
    {
      icon: '🛡️',
      title: 'Security by Design',
      description: 'Every solution we build prioritizes security, compliance, and data protection from day one.'
    },
    {
      icon: '📈',
      title: 'Results Focused',
      description: 'We measure success by the tangible business outcomes and performance improvements we deliver.'
    }
  ];

  const techStack = [
    {
      category: 'Cloud Platforms',
      technologies: [
        { name: 'AWS', icon: '☁️', description: 'Complete cloud infrastructure and services' },
        { name: 'Azure', icon: '🔷', description: 'Microsoft cloud platform and solutions' },
        { name: 'GCP', icon: '🌐', description: 'Google Cloud Platform and services' },
        { name: 'Multi-Cloud', icon: '🌈', description: 'Hybrid and multi-cloud strategies' }
      ]
    },
    {
      category: 'Containerization & Orchestration',
      technologies: [
        { name: 'Docker', icon: '🐳', description: 'Containerization and microservices' },
        { name: 'Kubernetes', icon: '⚓', description: 'Container orchestration and scaling' },
        { name: 'Helm', icon: '🪖', description: 'Package management for Kubernetes' },
        { name: 'OpenShift', icon: '🔴', description: 'Enterprise Kubernetes platform' }
      ]
    },
    {
      category: 'CI/CD & Automation',
      technologies: [
        { name: 'Jenkins', icon: '🤖', description: 'Continuous integration and deployment' },
        { name: 'GitLab CI', icon: '🦊', description: 'GitLab pipelines and automation' },
        { name: 'GitHub Actions', icon: '🐙', description: 'GitHub workflow automation' },
        { name: 'Argo CD', icon: '🚢', description: 'GitOps continuous delivery for Kubernetes' },
        { name: 'Scripting', icon: '📝', description: 'Bash, Python, and automation scripts' }
      ]
    },
    {
      category: 'Infrastructure as Code',
      technologies: [
        { name: 'Terraform', icon: '🏗️', description: 'Infrastructure provisioning and management' },
        { name: 'Ansible', icon: '⚙️', description: 'Configuration management and automation' },
        { name: 'CloudFormation', icon: '📋', description: 'AWS infrastructure templates' },
        { name: 'Pulumi', icon: '🔧', description: 'Modern infrastructure as code with programming languages' }
      ]
    },
    {
      category: 'Monitoring & Observability',
      technologies: [
        { name: 'Prometheus', icon: '📊', description: 'Metrics collection and monitoring' },
        { name: 'Grafana', icon: '📈', description: 'Data visualization and dashboards' },
        { name: 'ELK Stack', icon: '🔍', description: 'Log aggregation and analysis' },
        { name: 'Jaeger', icon: '🔍', description: 'Distributed tracing and APM' }
      ]
    },
    {
      category: 'Security & Compliance',
      technologies: [
        { name: 'AWS IAM', icon: '🔐', description: 'Identity and access management' },
        { name: 'Vault', icon: '🗝️', description: 'Secrets management and encryption' },
        { name: 'Security Hub', icon: '🛡️', description: 'Security monitoring and compliance' },
        { name: 'Zero Trust', icon: '🛡️', description: 'Modern security architecture and policies' }
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="relative text-white overflow-hidden min-h-screen">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('/images/photo-1667372283545-1261fb5c427a.png')",
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/80 to-blue-900/80"></div>
        </div>
        
        <div className="relative max-w-[83rem] mx-[1rem] sm:mx-[2rem] md:mx-[6rem] py-20 min-h-screen flex items-center">
          <div className="grid lg:grid-cols-2 gap-12 items-center w-full">
            <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-8">
                We're Not Just Another
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400"> DevOps Company</span>
              </h1>
              <p className="text-xl mb-8 opacity-90 leading-relaxed">
                We're the architects of digital transformation, the guardians of cloud efficiency, and the catalysts of innovation that power the world's most ambitious companies.
              </p>
              <p className="text-lg mb-8 opacity-80">
                At RogerOps, we believe that exceptional technology should be accessible to every business. We bridge the gap between complex cloud infrastructure and seamless business operations.
              </p>
              {/* <div className="flex flex-col sm:flex-row gap-4">
                <button className="bg-white text-purple-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                  Our Story
                </button>
                <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-purple-600 transition-colors">
                  Meet the Team
                </button>
              </div> */}
            </div>
            
            {/* Animated Team Showcase */}
            <div className={`transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="relative">
                <div className="bg-white rounded-lg shadow-xl overflow-hidden max-w-sm mx-auto">
                  <div className="relative h-80 overflow-hidden">
                    <img 
                      src={teamImages[currentImageIndex]} 
                      alt="RogerOps Team"
                      className="w-full h-full object-cover transition-opacity duration-1000"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-white font-bold text-lg">Our Mission</h3>
                      <p className="text-white/90 text-sm">Empowering businesses through innovative cloud solutions</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Numbers That Tell Our Story
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our impact is measured not just in code deployed, but in businesses transformed and challenges overcome.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-purple-600 mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div className="bg-gray-50 py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Our Core Values
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              These principles guide every decision we make and every solution we deliver.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow">
                <div className="text-4xl mb-4">{value.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Technology Stack Section */}
      <div className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Our Core Tech Expertise
            </h2>
            <p className="text-lg text-gray-600">
              We master the tools and technologies that power modern cloud infrastructure and DevOps practices.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {techStack.map((category, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow">
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">{category.category}</h3>
                
                <div className="space-y-4">
                  {category.technologies.map((tech, techIndex) => (
                    <div key={techIndex} className="bg-white rounded-lg p-4 border border-gray-200 hover:border-purple-300 transition-colors">
                      <div className="flex items-center mb-2">
                        <span className="text-2xl mr-3">{tech.icon}</span>
                        <h4 className="font-semibold text-gray-900">{tech.name}</h4>
                      </div>
                      <p className="text-gray-600 text-sm ml-11">{tech.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Team Section */}
      <div className="bg-gray-50 py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Meet the Minds Behind RogerOps
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our team combines decades of experience in cloud architecture, DevOps engineering, and business transformation.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-lg text-center">
              <div className="w-24 h-24 bg-purple-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-3xl">👨‍💻</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Cloud Architects</h3>
              <p className="text-gray-600">Experts in AWS, Azure, and GCP who design scalable, secure, and cost-effective solutions.</p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-lg text-center">
              <div className="w-24 h-24 bg-blue-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-3xl">🔧</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">DevOps Engineers</h3>
              <p className="text-gray-600">Automation specialists who streamline your development and deployment processes.</p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-lg text-center">
              <div className="w-24 h-24 bg-green-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-3xl">💰</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">FinOps Experts</h3>
              <p className="text-gray-600">Cost optimization specialists who ensure you get maximum value from your cloud investment.</p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to Transform Your Infrastructure?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join hundreds of companies who trust RogerOps to power their digital transformation.
          </p>
          <div className="flex justify-center">
            <button 
              onClick={() => setShowScheduleModal(true)}
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-purple-600 transition-colors"
            >
              Schedule a Call
            </button>
          </div>
        </div>
      </div>



      {/* Schedule a Call Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 relative">
            <button
              onClick={() => setShowScheduleModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
            <div className="text-center">
              <div className="text-4xl mb-4">📞</div>
              <h3 className="text-2xl font-bold text-black mb-4">Contact RogerOps</h3>
              <p className="text-gray-600 mb-6">
                Get in touch with our cloud experts for a consultation or to discuss your infrastructure needs.
              </p>
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                  <div className="flex items-center justify-center mb-2">
                    <span className="text-2xl mr-2">📧</span>
                    <span className="font-semibold text-gray-900">Email</span>
                  </div>
                  <a 
                    href="mailto:contact@rogerops.com?subject=Inquiry from RogerOps Website&body=Hello RogerOps team,%0D%0A%0D%0AI would like to discuss my cloud infrastructure needs.%0D%0A%0D%0ABest regards," 
                    className="text-[#3498db] hover:text-[#2980b9] font-medium underline cursor-pointer block py-2 px-4 rounded border border-blue-200 hover:bg-blue-50 transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    contact@rogerops.com
                  </a>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                  <div className="flex items-center justify-center mb-2">
                    <span className="text-2xl mr-2">📱</span>
                    <span className="font-semibold text-gray-900">Phone</span>
                  </div>
                  <a 
                    href="tel:+91-8010989509" 
                    className="text-[#3498db] hover:text-[#2980b9] font-medium underline cursor-pointer block py-2 px-4 rounded border border-blue-200 hover:bg-blue-50 transition-colors"
                  >
                    +91-8010989509
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AboutUsNew; 